import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import Icon from '../../Icons'

import { getLocationId } from './helpers'
import { useDigitalSignageTable } from './hooks'

const DigitalSignageTable = ({ locations, setTvApp, buildingId, refetchLocations }) => {
  const { t } = useTranslation(['common'])
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useDigitalSignageTable({ locations: locations, setTvApp })

  const [editingCellId, setEditingCellId] = useState(null)
  const [locationName, setLocationName] = useState('')
  const [isLocationValid, setIsLocationValid] = useState(true)

  const onChangeLocationName = (e) => {
    const pattern = new RegExp('^[A-Za-z0-9 -]+$')
    setIsLocationValid(pattern.test(e.target.value))
    if (e.target.value.length > 24) return
    setLocationName(e.target.value)
  }

  const onSaveName = async (editUrl) => {
    const locationId = getLocationId(editUrl)
    if (!locationId) return
    try {
      await API.updateDigitalSignageLocationName(locationId, locationName)
      await refetchLocations(buildingId)
    } catch (error) {
      console.error('Error', error)
    } finally {
      setEditingCellId(null)
      setLocationName('')
    }
  }

  const tableProps = getTableProps()
  const { key: tableKey, ...restTableProps } = tableProps

  return (
    <table key={tableKey} {...restTableProps} className="digital-signage-table">
      <thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps()
          const { key: headerGroupKey, ...restHeaderGroupProps } = headerGroupProps
          return (
            <tr key={headerGroupKey} {...restHeaderGroupProps}>
              {headerGroup.headers.map((column) => {
                const columnProps = column.getHeaderProps()
                const { key: columnKey, ...restColumnProps } = columnProps
                return (
                  <th key={columnKey} {...restColumnProps}>
                    {column.render('Header')}
                  </th>
                )
              })}
            </tr>
          )
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row)
          const rowProps = row.getRowProps()
          const { key: rowKey, ...restRowProps } = rowProps
          return (
            <tr key={rowKey} {...restRowProps}>
              {row.cells.map((cell) => {
                const cellProps = cell.getCellProps()
                const { key: cellKey, ...restCellProps } = cellProps

                if (cell.column.id === 'location') {
                  const isEditing = editingCellId === cell.row.id
                  return (
                    <td key={cellKey} {...restCellProps}>
                      <div
                        className={`location-name ${
                          isEditing ? 'location-name-edit' : 'location-name-save'
                        }`}
                      >
                        <div className="location-name-text">
                          {isEditing ? (
                            <input
                              pattern="[A-Za-z0-9 -]+"
                              value={locationName}
                              onChange={onChangeLocationName}
                            />
                          ) : (
                            cell.render('Cell')
                          )}
                        </div>
                        <div className="location-name-btn">
                          {isEditing ? (
                            <button
                              onClick={() => onSaveName(cell.row.original.editUrl)}
                              disabled={!isLocationValid}
                            >
                              {t('common:actions.save')}
                            </button>
                          ) : (
                            <div
                              className="edit-location-name-btn"
                              onClick={() => {
                                setEditingCellId(cell.row.id)
                                setLocationName(cell.value)
                              }}
                            >
                              {/* Note: the editPen path only occupies x 8.6-19.3 / y 8-18.7 of its
                                  20x20 box - a same-size viewBox shifted onto the glyph's center
                                  keeps its size but centers it vertically against the text. */}
                              <Icon icon="editPen" viewBox="4 3.4 20 20" />
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  )
                }

                return (
                  <td key={cellKey} {...restCellProps}>
                    <div>{cell.render('Cell')}</div>
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

DigitalSignageTable.propTypes = {
  buildingId: PropTypes.number.isRequired,
  locations: PropTypes.array.isRequired,
  refetchLocations: PropTypes.func.isRequired,
  setTvApp: PropTypes.func.isRequired,
}

export default DigitalSignageTable
